import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  ArrowLeft,
  Globe,
  Shield,
  Wrench,
  Database,
  Clock,
  Save,
  RefreshCw,
  Copy,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Upload,
  Check,
  Trash2,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Switch } from '../ui/switch'
import { toast } from 'react-hot-toast'

const SiteSettings = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)
  const [faviconFile, setFaviconFile] = useState(null)
  const [faviconPreview, setFaviconPreview] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)

  const { data: site, isLoading, error } = useQuery(
    ['site', id],
    async () => {
      const response = await apiService.get(`/sites/${id}`)
      return response
    },
    {
      enabled: !!id,
      onSuccess: (data) => { console.log('Site data received:', data) },
      onError: (error) => { console.error('Error fetching site data:', error) }
    }
  )

  const [settings, setSettings] = useState({
    maintenanceMode: false,
    maintenanceMessage: 'This website is currently under maintenance. Please check back later.',
    passwordProtection: false,
    password: '',
    searchIndexing: true,
    customCode: { headerCode: '', footerCode: '', analyticsCode: '' }
  })

  useEffect(() => {
    if (site) {
      const meta = site?.data?.meta_data || {}
      setSettings({
        maintenanceMode: meta.maintenanceMode || false,
        maintenanceMessage: meta.maintenanceMessage || 'This website is currently under maintenance. Please check back later.',
        passwordProtection: meta.passwordProtection || false,
        password: meta.accessPassword || '',
        searchIndexing: meta.searchIndexing !== false,
        customCode: {
          headerCode: meta.customCode?.headerCode || '',
          footerCode: meta.customCode?.footerCode || '',
          analyticsCode: meta.customCode?.analyticsCode || ''
        }
      })
    }
  }, [site])

  const updateSettingsMutation = useMutation(
    (newSettings) => apiService.put(`/sites/${id}/settings`, newSettings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
        toast.success('Site settings updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings')
        setIsSaving(false)
      }
    }
  )

  const uploadFaviconMutation = useMutation(
    async (formData) => {
      const response = await apiService.post('/clients/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Favicon uploaded successfully!')
        queryClient.invalidateQueries(['site', id])
        setFaviconFile(null)
        setFaviconPreview(null)
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to upload favicon')
    }
  )

  const uploadLogoMutation = useMutation(
    async (formData) => {
      const uploadRes = await apiService.post('/media/upload', formData)
      const url = uploadRes.data?.files?.[0]?.public_url
      if (!url) throw new Error('No URL returned from upload')
      const rawMeta = site?.data?.meta_data
      const existingMeta = rawMeta ? (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta) : {}
      await apiService.put(`/sites/${id}`, { meta_data: JSON.stringify({ ...existingMeta, logo: url }) })
      return url
    },
    {
      onSuccess: () => {
        toast.success('Site logo uploaded successfully!')
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
        setLogoFile(null)
        setLogoPreview(null)
      },
      onError: (error) => toast.error(error.response?.data?.error || error.message || 'Failed to upload logo')
    }
  )

  const removeLogoMutation = useMutation(
    async () => {
      const rawMeta = site?.data?.meta_data
      const existingMeta = rawMeta ? (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta) : {}
      const { logo: _removed, ...metaWithoutLogo } = existingMeta
      await apiService.put(`/sites/${id}`, { meta_data: JSON.stringify(metaWithoutLogo) })
    },
    {
      onSuccess: () => {
        toast.success('Site logo removed')
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to remove logo')
    }
  )

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleLogoUpload = () => {
    if (!logoFile) return
    const formData = new FormData()
    formData.append('files', logoFile)
    const clientId = site?.data?.client_id
    if (clientId) formData.append('client_id', clientId)
    uploadLogoMutation.mutate(formData)
  }

  const handleFaviconChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFaviconFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setFaviconPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleFaviconUpload = () => {
    if (!faviconFile) return
    const clientId = site?.data?.client_id
    const formData = new FormData()
    formData.append('favicon', faviconFile)
    if (clientId) formData.append('clientId', clientId)
    uploadFaviconMutation.mutate(formData)
  }

  const handleSave = () => {
    setIsSaving(true)
    updateSettingsMutation.mutate(settings)
  }

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all settings to default values?')) {
      setSettings({
        maintenanceMode: false,
        maintenanceMessage: 'This website is currently under maintenance. Please check back later.',
        passwordProtection: false,
        password: '',
        searchIndexing: true,
        customCode: { headerCode: '', footerCode: '', analyticsCode: '' }
      })
      toast.success('Settings reset to defaults')
    }
  }

  const siteUrl = site?.data?.assigned_domain ? `https://${site.data.assigned_domain}` : null

  const copyUrlToClipboard = () => {
    if (siteUrl) {
      navigator.clipboard.writeText(siteUrl)
      toast.success('Site URL copied to clipboard')
    }
  }

  const getStatusText = (status) => {
    if (!status) return 'UNKNOWN'
    try { return String(status).toUpperCase() } catch { return 'UNKNOWN' }
  }

  if (isLoading) {
    return (
      <div style={{ paddingTop: 16 }}>
        <div className="flex items-center justify-center" style={{ height: 256 }}>
          <LoadingSpinner size="lg" text="Loading site settings..." />
        </div>
      </div>
    )
  }

  if (error || (!isLoading && !site)) {
    return (
      <div style={{ paddingTop: 16 }}>
        <div className="card card-pad" style={{ border: '1px solid var(--danger)', textAlign: 'center' }}>
          <AlertTriangle style={{ width: 48, height: 48, color: 'var(--danger)', margin: '0 auto 16px' }} />
          <h3 className="font-semibold" style={{ color: 'var(--danger)', marginBottom: 8 }}>Site Not Found</h3>
          <p className="text-soft" style={{ fontSize: 13, marginBottom: 16 }}>
            The requested site could not be found or you don't have permission to access it.
          </p>
          <button className="btn" onClick={() => navigate('/admin/sites')}>
            <ArrowLeft style={{ width: 16, height: 16, marginRight: 8 }} />Back to Sites
          </button>
        </div>
      </div>
    )
  }

  if (isLoading || !site) {
    return (
      <div style={{ paddingTop: 16 }}>
        <div className="flex items-center justify-center" style={{ height: 256 }}>
          <LoadingSpinner size="lg" text="Loading site settings..." />
        </div>
      </div>
    )
  }

  const rawMeta = site?.data?.meta_data
  const parsedMeta = rawMeta
    ? (typeof rawMeta === 'string' ? (() => { try { return JSON.parse(rawMeta) } catch { return {} } })() : rawMeta)
    : {}
  const currentLogo = parsedMeta?.logo || null

  return (
    <div style={{ paddingTop: 16 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ display: 'flex', flexDirection: 'column', gap: 32 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => navigate('/admin/sites')}>
              <ArrowLeft style={{ width: 20, height: 20 }} />
            </button>
            <div>
              <h1 className="h2">{site?.data?.name || 'Site'} Settings</h1>
              <p className="text-muted" style={{ fontSize: 13 }}>Configure website settings and management options</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="btn" onClick={handleReset}>
              <RefreshCw style={{ width: 16, height: 16, marginRight: 8 }} />Reset
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <RefreshCw style={{ width: 16, height: 16, marginRight: 8, animation: 'spin 0.6s linear infinite' }} /> : <Save style={{ width: 16, height: 16, marginRight: 8 }} />}
              Save Settings
            </button>
          </div>
        </div>

        {/* Site Info */}
        <div className="card card-pad">
          <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
            <Globe style={{ width: 18, height: 18, color: 'var(--accent)' }} />
            <span className="font-semibold">Site Information</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Site Name</label>
              <p className="font-semibold">{site?.data?.name || 'Unnamed Site'}</p>
            </div>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Company</label>
              <p className="font-semibold">{site?.data?.company_name || 'Unknown Company'}</p>
            </div>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Status</label>
              <span className={
                site?.data?.status === 'published' ? 'badge badge-success' :
                site?.data?.status === 'draft' ? 'badge badge-warning' : 'badge'
              } style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {site?.data?.status === 'published' && <CheckCircle style={{ width: 10, height: 10 }} />}
                {site?.data?.status === 'draft' && <Clock style={{ width: 10, height: 10 }} />}
                {site?.data?.status === 'archived' && <XCircle style={{ width: 10, height: 10 }} />}
                {getStatusText(site?.data?.status)}
              </span>
            </div>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Site URL</label>
              <div className="flex items-center gap-2">
                {siteUrl ? (
                  <>
                    <code style={{ padding: '2px 8px', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>{siteUrl}</code>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={copyUrlToClipboard}>
                      <Copy style={{ width: 14, height: 14 }} />
                    </button>
                    {site?.data?.status === 'published' && (
                      <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-icon btn-sm">
                        <ExternalLink style={{ width: 14, height: 14 }} />
                      </a>
                    )}
                  </>
                ) : (
                  <span className="text-muted" style={{ fontSize: 13, fontStyle: 'italic' }}>No domain assigned yet</span>
                )}
              </div>
              {siteUrl && (
                <p className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                  To change the primary domain, go to the client's Domain Management page.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Favicon */}
        <div className="card card-pad">
          <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
            <Globe style={{ width: 18, height: 18, color: 'var(--accent)' }} />
            <span className="font-semibold">Favicon</span>
            {site?.data?.favicon && (
              <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Check style={{ width: 10, height: 10 }} />Uploaded
              </span>
            )}
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 16 }}>The icon shown in browser tabs and bookmarks for this client's websites</p>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Current Favicon</label>
              <div style={{ width: 80, height: 80, border: '2px dashed var(--border)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                {site?.data?.favicon ? (
                  <img src={site.data.favicon} alt="Favicon" style={{ width: 40, height: 40, objectFit: 'contain' }} />
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <Globe style={{ width: 22, height: 22, color: 'var(--text-faint)', margin: '0 auto 2px' }} />
                    <p className="text-muted" style={{ fontSize: 10 }}>None</p>
                  </div>
                )}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Upload New Favicon</label>
              <p className="text-muted" style={{ fontSize: 11, marginBottom: 10 }}>Accepts .ico, .png, or .svg. Square image recommended (32×32px). Applied to all sites for this client.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  type="file"
                  accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
                  onChange={handleFaviconChange}
                  style={{ fontSize: 13, color: 'var(--text-soft)' }}
                />
                {faviconPreview && (
                  <div style={{ width: 80, height: 80, border: '2px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                    <img src={faviconPreview} alt="Preview" style={{ width: 40, height: 40, objectFit: 'contain' }} />
                  </div>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleFaviconUpload}
                  disabled={!faviconFile || uploadFaviconMutation.isLoading}
                  style={{ alignSelf: 'flex-start' }}
                >
                  {uploadFaviconMutation.isLoading ? (
                    <><RefreshCw style={{ width: 12, height: 12, marginRight: 6, animation: 'spin 0.6s linear infinite' }} />Uploading...</>
                  ) : (
                    <><Upload style={{ width: 12, height: 12, marginRight: 6 }} />Upload Favicon</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Site Logo */}
        <div className="card card-pad">
          <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
            <Globe style={{ width: 18, height: 18, color: 'var(--accent)' }} />
            <span className="font-semibold">Site Logo</span>
            {currentLogo && (
              <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Check style={{ width: 10, height: 10 }} />Uploaded
              </span>
            )}
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 16 }}>The logo displayed on this site's cards and in the content editor. Changing it here will also update the editor.</p>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Current Logo</label>
              <div style={{ width: 80, height: 80, border: '2px dashed var(--border)', borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                {currentLogo ? (
                  <img src={currentLogo} alt="Site Logo" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 'var(--radius-sm)' }} />
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <Globe style={{ width: 22, height: 22, color: 'var(--text-faint)', margin: '0 auto 2px' }} />
                    <p className="text-muted" style={{ fontSize: 10 }}>None</p>
                  </div>
                )}
              </div>
              {currentLogo && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => removeLogoMutation.mutate()}
                  disabled={removeLogoMutation.isLoading}
                  style={{ width: 80, marginTop: 8, color: 'var(--danger)', fontSize: 12 }}
                >
                  {removeLogoMutation.isLoading ? (
                    <RefreshCw style={{ width: 12, height: 12, animation: 'spin 0.6s linear infinite' }} />
                  ) : (
                    <><Trash2 style={{ width: 12, height: 12, marginRight: 4 }} />Remove</>
                  )}
                </button>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Upload New Logo</label>
              <p className="text-muted" style={{ fontSize: 11, marginBottom: 10 }}>Accepts .png, .jpg, .svg, or .webp. Transparent PNG recommended. Used on site cards and the content editor.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleLogoChange}
                  style={{ fontSize: 13, color: 'var(--text-soft)' }}
                />
                {logoPreview && (
                  <div style={{ width: 80, height: 80, border: '2px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                    <img src={logoPreview} alt="Preview" style={{ width: 56, height: 56, objectFit: 'contain' }} />
                  </div>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleLogoUpload}
                  disabled={!logoFile || uploadLogoMutation.isLoading}
                  style={{ alignSelf: 'flex-start' }}
                >
                  {uploadLogoMutation.isLoading ? (
                    <><RefreshCw style={{ width: 12, height: 12, marginRight: 6, animation: 'spin 0.6s linear infinite' }} />Uploading...</>
                  ) : (
                    <><Upload style={{ width: 12, height: 12, marginRight: 6 }} />Upload Logo</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Maintenance Mode */}
        <div className="card card-pad">
          <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
            <Wrench style={{ width: 18, height: 18, color: 'var(--accent)' }} />
            <span className="font-semibold">Maintenance Mode</span>
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 16 }}>Put your website in maintenance mode to perform updates</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enable Maintenance Mode</p>
                <p className="text-soft" style={{ fontSize: 13 }}>Show a maintenance page instead of your website</p>
              </div>
              <Switch
                checked={settings.maintenanceMode}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, maintenanceMode: checked }))}
              />
            </div>
            {settings.maintenanceMode && (
              <div>
                <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Maintenance Message</label>
                <textarea
                  value={settings.maintenanceMessage}
                  onChange={(e) => setSettings(prev => ({ ...prev, maintenanceMessage: e.target.value }))}
                  rows={3}
                  className="input"
                  placeholder="Enter the message visitors will see during maintenance..."
                  style={{ resize: 'none' }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Access Control */}
        <div className="card card-pad">
          <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
            <Shield style={{ width: 18, height: 18, color: 'var(--accent)' }} />
            <span className="font-semibold">Access Control</span>
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 16 }}>Control who can access your website</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Password Protection</p>
                <p className="text-soft" style={{ fontSize: 13 }}>Require a password to access the website</p>
              </div>
              <Switch
                checked={settings.passwordProtection}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, passwordProtection: checked }))}
              />
            </div>
            {settings.passwordProtection && (
              <div>
                <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Access Password</label>
                <input
                  type="password"
                  value={settings.password}
                  onChange={(e) => setSettings(prev => ({ ...prev, password: e.target.value }))}
                  className="input"
                  placeholder="Enter access password..."
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Search Engine Indexing</p>
                <p className="text-soft" style={{ fontSize: 13 }}>Allow search engines to index this website</p>
              </div>
              <Switch
                checked={settings.searchIndexing}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, searchIndexing: checked }))}
              />
            </div>
          </div>
        </div>

        {/* Custom Code */}
        <div className="card card-pad">
          <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
            <Database style={{ width: 18, height: 18, color: 'var(--accent)' }} />
            <span className="font-semibold">Custom Code</span>
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 16 }}>Add custom HTML, CSS, or JavaScript to your website</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Header Code (Before &lt;/head&gt;)</label>
              <textarea
                value={settings.customCode.headerCode}
                onChange={(e) => setSettings(prev => ({ ...prev, customCode: { ...prev.customCode, headerCode: e.target.value } }))}
                rows={4}
                className="input"
                placeholder="<!-- Custom CSS, meta tags, etc. -->"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
              />
            </div>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Footer Code (Before &lt;/body&gt;)</label>
              <textarea
                value={settings.customCode.footerCode}
                onChange={(e) => setSettings(prev => ({ ...prev, customCode: { ...prev.customCode, footerCode: e.target.value } }))}
                rows={4}
                className="input"
                placeholder="<!-- Custom JavaScript, tracking codes, etc. -->"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
              />
            </div>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Analytics Code</label>
              <textarea
                value={settings.customCode.analyticsCode}
                onChange={(e) => setSettings(prev => ({ ...prev, customCode: { ...prev.customCode, analyticsCode: e.target.value } }))}
                rows={3}
                className="input"
                placeholder="<!-- Google Analytics, Facebook Pixel, etc. -->"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default SiteSettings
